<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { PermissionOrganization, Query } from '@/generated/graphql'
import gql from 'graphql-tag'
import { useRoute, useRouter } from 'vue-router'
import Breadcrumb from '@/components/Breadcrumb.vue'
import EbomPartDetails from '@/components/ebom/EbomPartDetails.vue'
import EbomDiffDialog from '@/components/ebom/EbomDiffDialog.vue'
import BulkAllocationSuggestionActions from '@/components/ebomPartAllocationSuggestions/BulkAllocationSuggestionActions.vue'
import EbomBulkAllocateDialog from '@/components/ebom/EbomBulkAllocateDialog.vue'
import EbomModelViewer from '@/components/ebom/EbomModelViewer.vue'
import { ComponentExposed } from 'vue-component-type-helpers'
import useAuthorization from '@/composables/useAuthorization'
import ArchivedConfigurationAlert from '@/components/archive/ArchivedConfigurationAlert.vue'
import EbomPartTree from '@/components/ebom/EbomPartTree.vue'
import { useVertResize, useHorizResize } from '@/composables/useResizable'

const { t } = useI18n()
const { hasOrgAuthz } = useAuthorization()
const route = useRoute()
const router = useRouter()

const productConfigId = computed(() => route.params.productConfigId as string)
const bopId = computed(() => route.params.bopId as string)
const ebomId = computed(() => route.params.ebomId as string)
const ebomPartId = computed(() => route.params.ebomPartId as string | undefined)
const fetchQuery = useQuery<Query>(
  gql`
    query getEbom($id: ID!) {
      article {
        ebom(id: $id) {
          id
          name
          sourceId
          sourceType
          ebomParts {
            id
            articleNumber
            articlePath {
              articleNumbers
            }
          }
        }
      }
    }
  `,
  () => ({
    id: ebomId.value,
  }),
  () => ({
    enabled: !!ebomId.value,
  }),
)
const ebom = computed(() => fetchQuery.result.value?.article.ebom)

const selectedPart = computed(() => {
  if (!ebomPartId.value || !ebom.value) return null
  return ebom.value.ebomParts.find((p: { id: string }) => p.id === ebomPartId.value) ?? null
})

function selectEbomPartId(id: string | undefined) {
  router.push({ params: { ebomPartId: id } })
}

const menuOpen = ref(false)

const bulkAllocateDialog = ref<ComponentExposed<typeof EbomBulkAllocateDialog>>()

// Resizable splits
const {
  pct: leftColPct,
  containerEl: mainRow,
  startDrag: startHorizDrag,
} = useHorizResize(33, 20, 75)
const { pct: viewerPct, containerEl: leftCol, startDrag: startVertDrag } = useVertResize(50, 15, 75)
</script>

<template>
  <div class="ebom-page">
    <breadcrumb />
    <archived-configuration-alert :product-configuration-id="productConfigId" />

    <v-menu v-model="menuOpen" :close-on-content-click="false">
      <template #activator="{ props }">
        <v-btn color="primary" v-bind="props" icon="menu" class="ebom-menu-btn" />
      </template>
      <v-list min-width="600">
        <v-list-item v-if="hasOrgAuthz(PermissionOrganization.OrgProductWrite)" class="text-right">
          <v-btn class="mb-1" @click="bulkAllocateDialog?.open()">
            {{ t('component.ebomBulkAllocateDialog.title') }}
          </v-btn>
        </v-list-item>
        <v-list-item>
          <ebom-diff-dialog :bop-id="bopId" :current-ebom-id="ebomId" @close="menuOpen = false" />
        </v-list-item>
        <bulk-allocation-suggestion-actions
          v-if="hasOrgAuthz(PermissionOrganization.OrgProductWrite)"
          :ebom-id="ebomId"
        />
      </v-list>
    </v-menu>

    <ebom-bulk-allocate-dialog ref="bulkAllocateDialog" :ebom-id="ebomId" :bop-id="bopId" />

    <h1>{{ t('entity.ebom.singular') }} {{ ebom?.name }}</h1>

    <div ref="mainRow" class="mt-1 ebom-layout">
      <div ref="leftCol" class="left-col" :style="{ width: leftColPct + '%' }">
        <div class="overflow-y-auto overflow-x-hidden" style="flex: 1; min-height: 0">
          <ebom-part-tree
            v-if="ebomId"
            :ebom-id="ebomId"
            :model-value="ebomPartId"
            @update:model-value="selectEbomPartId"
          />
        </div>

        <div class="resize-handle-h" @mousedown="startVertDrag" />

        <div :style="{ height: viewerPct + '%', minHeight: '150px' }">
          <ebom-model-viewer
            :ebom-id="ebomId"
            :article-number="selectedPart?.articleNumber"
            :article-path="selectedPart?.articlePath?.articleNumbers"
          />
        </div>
      </div>

      <div class="resize-handle-v" @mousedown="startHorizDrag" />

      <div class="overflow-y-auto px-3" style="flex: 1; min-height: 0">
        <v-empty-state v-if="!ebomPartId" icon="account_tree" text="Select a part" />
        <ebom-part-details v-if="ebomPartId" :bop-id="bopId" :ebom-part-id="ebomPartId" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.ebom-page {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;

  :deep(.breadcrumb-container) {
    top: 0;
  }
}

.ebom-menu-btn {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
}

.ebom-layout {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.left-col {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}
</style>
